import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';

/**
 * PostLoginRouter
 *
 * Handles routing after Kinde authentication based on user state:
 * 1. Check if onboarding is complete -> if not, go to /onboarding
 * 2. Check if user has brands -> if not, go to /onboarding
 * 3. If has brands -> go to /select-brand
 */
// Admin emails skip onboarding and go straight to dashboard
const ADMIN_EMAILS = (import.meta.env.VITE_ADMIN_EMAILS || 'steve@gtmvp.com,steve@stevekaplan.ai').split(',').map((e: string) => e.trim())

export function PostLoginRouter() {
  const navigate = useNavigate();
  const { isAuthenticated, email, getAccessToken, isLoading: authLoading } = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkUserState = async () => {
      if (authLoading) return;

      if (!isAuthenticated) {
        navigate('/auth/login');
        return;
      }

      // Admin users skip onboarding
      if (email && ADMIN_EMAILS.includes(email)) {
        navigate('/dashboard');
        return;
      }

      try {
        const token = await getAccessToken();

        // Set token on axios defaults so ALL subsequent API calls are authenticated
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        }
        const baseUrl = import.meta.env.PROD
          ? 'https://marketing-ai-platform-production.up.railway.app'
          : import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:4000';

        // Check onboarding status
        const onboardingResponse = await fetch(`${baseUrl}/api/v1/onboarding/progress`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (onboardingResponse.ok) {
          const onboardingResult = await onboardingResponse.json();

          // If onboarding is not complete, redirect to onboarding
          if (onboardingResult.success && onboardingResult.data) {
            const { status, completionPercent } = onboardingResult.data;

            if (status !== 'completed' && status !== 'skipped' && completionPercent < 100) {
              navigate('/onboarding');
              return;
            }
          }
        }

        // Check if user has brands
        const brandsResponse = await fetch(`${baseUrl}/api/v1/brands`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (brandsResponse.ok) {
          const brandsResult = await brandsResponse.json();

          if (brandsResult.success) {
            const brands = brandsResult.data || [];

            if (brands.length === 0) {
              // No brands - go to onboarding wizard
              navigate('/onboarding');
              return;
            }

            // Has brands - go to brand selector
            navigate('/select-brand');
            return;
          }
        }

        // Fallback to dashboard if checks fail
        navigate('/dashboard');
      } catch (err) {
        console.error('Error checking user state:', err);
        setError(err instanceof Error ? err.message : 'Failed to check user state');
        // Fallback to dashboard on error
        setTimeout(() => navigate('/dashboard'), 2000);
      } finally {
        setIsChecking(false);
      }
    };

    checkUserState();
  }, [isAuthenticated, authLoading, getAccessToken, navigate]);

  if (authLoading || isChecking) {
    return (
      <div className="min-h-screen bg-[#080F14] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-300">Setting up your workspace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#080F14] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-2">Error: {error}</p>
          <p className="text-gray-400">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return null;
}

export default PostLoginRouter;
